import { useState } from 'react';
import { Plus, Download, Pencil, History, Trash2, ListPlus } from 'lucide-react';
import { api } from '../../lib/api';
import { countWord } from '../../lib/countWord.mjs';
import Modal from '../ui/Modal';
import { Chip, downloadFile, inputCls } from '../bids/FieldGrid';
import { card, iconBtn, primaryBtn, useList, HistoryModal, Empty } from './shared';

const ROLE_LABEL = {
  owner_developer: 'Owner / developer', general_contractor: 'General contractor', subcontractor: 'Subcontractor',
  architect: 'Architect', design_firm: 'Design firm', engineer: 'Engineer', other: 'Other',
};

function describe(c = {}) {
  const parts = [];
  if (c.roles?.length) parts.push(c.roles.map((r) => ROLE_LABEL[r] || r).join(' or '));
  if (c.tiers?.length) parts.push(`on Tier ${c.tiers.join('/')} projects`);
  if (c.verticals?.length) parts.push(`vertical ${c.verticals.join(', ')}`);
  if (c.states?.length) parts.push(`in ${c.states.join(', ')}`);
  if (c.has_email) parts.push('with an email');
  return parts.join(', ') || 'All CRM companies';
}

function SegmentModal({ record, onSave, onClose }) {
  const [v, setV] = useState({
    name: record.name || '', description: record.description || '', kind: record.kind || 'static', notes: record.notes || '',
    tiers: record.criteria?.tiers || [], roles: record.criteria?.roles || [], states: (record.criteria?.states || []).join(', '),
    verticals: (record.criteria?.verticals || []).join(', '), has_email: !!record.criteria?.has_email,
  });
  const [err, setErr] = useState('');
  const toggle = (k, x) => setV({ ...v, [k]: v[k].includes(x) ? v[k].filter((y) => y !== x) : [...v[k], x] });
  const list = (s) => s.split(',').map((x) => x.trim()).filter(Boolean);
  async function save() {
    const body = { name: v.name, description: v.description || null, notes: v.notes || null };
    if (!record.id) body.kind = v.kind;
    if (v.kind === 'dynamic') body.criteria = { tiers: v.tiers, roles: v.roles, states: list(v.states), verticals: list(v.verticals), has_email: v.has_email };
    try { await onSave(body); } catch (e) { setErr(e.message); }
  }
  return (
    <Modal title={record.id ? 'Edit list' : 'New target list'} onClose={onClose} maxWidth="max-w-2xl">
      <div className="grid gap-3">
        <label className="text-sm"><span className="mb-1 block text-xs font-medium text-[var(--text-secondary)]">Name *</span><input className={inputCls} value={v.name} onChange={(e) => setV({ ...v, name: e.target.value })} /></label>
        <label className="text-sm"><span className="mb-1 block text-xs font-medium text-[var(--text-secondary)]">Description</span><input className={inputCls} value={v.description} onChange={(e) => setV({ ...v, description: e.target.value })} /></label>
        {!record.id && (
          <div className="flex gap-4 text-sm">
            <label className="flex items-center gap-2"><input type="radio" checked={v.kind === 'static'} onChange={() => setV({ ...v, kind: 'static' })} /> Hand-picked list</label>
            <label className="flex items-center gap-2"><input type="radio" checked={v.kind === 'dynamic'} onChange={() => setV({ ...v, kind: 'dynamic' })} /> Saved CRM filter</label>
          </div>
        )}
        {v.kind === 'dynamic' && (
          <div className="grid gap-3 rounded-lg border border-[var(--border)] p-3">
            <div className="text-xs text-[var(--text-secondary)]">Companies whose role on a Watchlist project matches (both role and tier on the same project).</div>
            <div className="flex flex-wrap gap-3 text-sm">{['A', 'B', 'C'].map((t) => <label key={t} className="flex items-center gap-1"><input type="checkbox" checked={v.tiers.includes(t)} onChange={() => toggle('tiers', t)} /> Tier {t}</label>)}</div>
            <div className="flex flex-wrap gap-3 text-sm">{Object.entries(ROLE_LABEL).map(([r, l]) => <label key={r} className="flex items-center gap-1"><input type="checkbox" checked={v.roles.includes(r)} onChange={() => toggle('roles', r)} /> {l}</label>)}</div>
            <label className="text-sm"><span className="mb-1 block text-xs font-medium text-[var(--text-secondary)]">States (comma separated)</span><input className={inputCls} value={v.states} onChange={(e) => setV({ ...v, states: e.target.value })} placeholder="MI, OH, VA" /></label>
            <label className="text-sm"><span className="mb-1 block text-xs font-medium text-[var(--text-secondary)]">Verticals (comma separated)</span><input className={inputCls} value={v.verticals} onChange={(e) => setV({ ...v, verticals: e.target.value })} placeholder="data-centers" /></label>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={v.has_email} onChange={(e) => setV({ ...v, has_email: e.target.checked })} /> Only contacts with an email</label>
          </div>
        )}
        <label className="text-sm"><span className="mb-1 block text-xs font-medium text-[var(--text-secondary)]">Notes</span><textarea className={inputCls} rows={3} value={v.notes} onChange={(e) => setV({ ...v, notes: e.target.value })} /></label>
      </div>
      {err && <p className="mt-2 text-sm text-rose-600">{err}</p>}
      <div className="mt-4 flex justify-end gap-2">
        <button onClick={onClose} className="rounded-lg border border-[var(--border)] px-3 py-1.5 text-sm">Cancel</button>
        <button onClick={save} className={primaryBtn}>Save</button>
      </div>
    </Modal>
  );
}

function SegmentDetail({ seg, onClose, onChanged }) {
  const { rows: d, err, load, setErr } = useList(`/marketing/segments/${seg.id}`, null);
  const [paste, setPaste] = useState('');
  const [result, setResult] = useState(null);
  async function importList() {
    try { const r = await api.post(`/marketing/segments/${seg.id}/import`, { text: paste }); setResult(r); setPaste(''); await load(); onChanged(); } catch (e) { setErr(e.message); }
  }
  const memberFor = (row) => d?.members?.find((m) => (row.contact_id ? m.contact_id === row.contact_id : m.account_id === row.account_id && !m.contact_id));
  return (
    <Modal title={seg.name} onClose={onClose} maxWidth="max-w-5xl">
      {err && <p className="text-sm text-rose-600">{err}</p>}
      {!d && !err && <p className="text-sm text-[var(--text-secondary)]">Loading…</p>}
      {d && (
        <>
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <div className="text-sm text-[var(--text-secondary)]">{d.segment.kind === 'dynamic' ? describe(d.segment.criteria) : 'Hand-picked list'} · {d.rows.length} {countWord(d.rows.length, 'row')}</div>
            <button className={iconBtn} onClick={() => downloadFile(`/marketing/segments/${seg.id}/export`, { method: 'POST', fallbackName: 'list.csv' }).catch((e) => setErr(e.message))}><Download size={12} /> Export CSV</button>
          </div>
          {d.segment.notes && <p className="mb-3 whitespace-pre-wrap text-xs text-[var(--text-secondary)]">{d.segment.notes}</p>}
          {d.segment.kind === 'static' && (
            <div className="mb-3 rounded-lg border border-[var(--border)] p-3">
              <div className="mb-1 text-xs font-medium text-[var(--text-secondary)]">Add from a list: one company name, email or web domain per line. Only CRM matches are added; add missing companies in Accounts first.</div>
              <textarea className={inputCls} rows={4} value={paste} onChange={(e) => setPaste(e.target.value)} />
              <div className="mt-2 flex items-center gap-2"><button className={primaryBtn} disabled={!paste.trim()} onClick={importList}><ListPlus size={14} /> Add matches</button>
                {result && <span className="text-xs text-[var(--text-secondary)]">Added {result.added}. {result.unmatched.length ? `Not in CRM: ${result.unmatched.slice(0, 15).join(', ')}${result.unmatched.length > 15 ? '…' : ''}` : 'All matched.'}</span>}</div>
            </div>
          )}
          <div className="max-h-[50vh] overflow-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-white dark:bg-slate-800"><tr className="text-left text-xs text-[var(--text-secondary)]"><th className="py-1 pr-2">Company</th><th className="pr-2">Roles / tiers</th><th className="pr-2">Contact</th><th className="pr-2">Email</th><th className="pr-2">State</th><th /></tr></thead>
              <tbody>
                {d.rows.map((r) => {
                  const m = memberFor(r);
                  return (
                    <tr key={`${r.account_id}|${r.contact_id}`} className="border-t border-[var(--border)]">
                      <td className="py-1 pr-2">{r.company || '(no company)'}</td>
                      <td className="pr-2 text-xs">{r.roles}{r.tiers ? ` · ${r.tiers}` : ''}</td>
                      <td className="pr-2">{r.contact_name}{r.title ? <span className="text-xs text-[var(--text-secondary)]"> · {r.title}</span> : null}</td>
                      <td className="pr-2 text-xs">{r.email}</td>
                      <td className="pr-2 text-xs">{r.state}</td>
                      <td>{m && <button className={iconBtn} title="Remove from list" onClick={async () => { try { await api.delete(`/marketing/segments/members/${m.id}`); await load(); onChanged(); } catch (e) { setErr(e.message); } }}><Trash2 size={12} /></button>}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {!d.rows.length && <Empty>No one on this list yet.</Empty>}
          </div>
        </>
      )}
    </Modal>
  );
}

export default function ListsTab({ onChanged }) {
  const { rows, err, load, setErr } = useList('/marketing/segments', 'segments');
  const [edit, setEdit] = useState(null);
  const [open, setOpen] = useState(null);
  const [hist, setHist] = useState(null);
  const save = async (body) => {
    if (edit.id) await api.patch(`/marketing/segments/${edit.id}`, body); else await api.post('/marketing/segments', body);
    setEdit(null); await load(); onChanged?.();
  };
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs text-[var(--text-secondary)]">Target lists built from the CRM. Export a list as CSV for your own email tool; the portal does not send campaigns.</p>
        <button className={primaryBtn} onClick={() => setEdit({})}><Plus size={14} /> New list</button>
      </div>
      {err && <p className="text-sm text-rose-600">{err}</p>}
      {!rows && !err && <p className="text-sm text-[var(--text-secondary)]">Loading…</p>}
      {rows && !rows.length && <Empty>No lists yet.</Empty>}
      <div className="grid gap-2 md:grid-cols-2">
        {(rows || []).map((s) => (
          <div key={s.id} className={card}>
            <div className="flex items-start justify-between gap-2">
              <button className="text-left" onClick={() => setOpen(s)}>
                <div className="font-medium text-[var(--text-primary)] hover:underline">{s.name}</div>
                <div className="text-xs text-[var(--text-secondary)]">{s.kind === 'dynamic' ? describe(s.criteria) : 'Hand-picked list'}</div>
              </button>
              <Chip tone={s.kind === 'dynamic' ? 'indigo' : 'gray'}>{s.kind === 'dynamic' ? 'CRM filter' : 'List'}</Chip>
            </div>
            <div className="mt-2 text-sm">{s.companies} {Number(s.companies) === 1 ? 'company' : 'companies'} · {s.contacts} {countWord(s.contacts, 'contact')} · {s.with_email} with email</div>
            {s.description && <div className="mt-1 text-xs text-[var(--text-secondary)]">{s.description}</div>}
            <div className="mt-2 flex flex-wrap gap-1">
              <button className={iconBtn} onClick={() => setOpen(s)}>Open</button>
              <button className={iconBtn} onClick={() => downloadFile(`/marketing/segments/${s.id}/export`, { method: 'POST', fallbackName: 'list.csv' }).catch((e) => setErr(e.message))}><Download size={12} /> CSV</button>
              <button className={iconBtn} onClick={() => setEdit(s)}><Pencil size={12} /> Edit</button>
              <button className={iconBtn} onClick={() => setHist(s)}><History size={12} /></button>
              <button className={iconBtn} onClick={async () => { if (!window.confirm(`Delete the list "${s.name}"?`)) return; try { await api.delete(`/marketing/segments/${s.id}`); await load(); onChanged?.(); } catch (e) { setErr(e.message); } }}><Trash2 size={12} /></button>
            </div>
          </div>
        ))}
      </div>
      {edit && <SegmentModal record={edit} onSave={save} onClose={() => setEdit(null)} />}
      {open && <SegmentDetail seg={open} onClose={() => setOpen(null)} onChanged={load} />}
      {hist && <HistoryModal table="mkt_segments" id={hist.id} title={hist.name} onClose={() => setHist(null)} />}
    </div>
  );
}
